import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import CarModel from '../../components/CarModel';
import SafeMount from '../../components/SafeMount';
import CarLights from '../../render/CarLights';
import { NEXT_LOOK } from '../../render/quality';
import { roadMapBridge } from '../../render/roadMapBridge';
import { ROAD } from '../../render/roadMap';
import { assetUrl } from '../../services/assets';
import { useWorldStore } from '../../state/worldStore';
import { moveBridge } from '../../state/moveBridge';
import { setCamPos, setCamBasis } from '../../services/worldObjects';
import { emitMove } from '../../services/worldSocket';
import { isTyping } from '../../utils/typing';
import { CITY_ROUTES } from '../../cityRoutes';
import { STREET_SPAWN } from '../../streetSpawn';
import { pushOut, obstacles, raycastWalls } from '../streetCollision';
import { engineStart, engineSet, engineStop, crash, door } from '../sfx';
import { useDriveStore, driveBridge, requestToggle } from './driveStore';
import { playerPos } from '../player';

// ── CARS YOU CAN DRIVE ───────────────────────────────────────────────────────
//
// A handful of cars parked at the kerb around the city — one within sight of
// where you arrive — that you can walk up to and take (F, or the on-screen
// button). Arcade handling: weight, grip that lets go under the handbrake, a
// chase camera that swings out behind you and opens its field of view with
// speed, walls that stop you with a crunch. The radio stays on; it is a music
// world, and the car is the best seat in it.
//
// Parking spots are read off the road map (the lane against the kerb, clear of
// the traffic routes), so they follow the city rather than living in a list.
// The cars are the traffic Elise in colours traffic never uses — never yellow,
// which belongs to Spor1's Lotus.
//
// Story safety: no driving while Spor1's ride is running; quick travel (the
// map) parks you first; rooms are walked into, not driven into.

const PAINTS = ['#c41e2a', '#0e1116', '#e9e9ee', '#1e4fc2', '#0c6e57', '#5b2a86'];
const COUNT = 6;
const CAR_LEN = 3.8;           // the Elise's real length (cars.json)
const MAX_FWD = 15;            // u/s ≈ 120 km/h in this world's scale
const MAX_REV = 5;
const ACCEL = 9;
const BRAKE = 24;
const WHEELBASE = 1.05;
const REACH = 1.6;             // how close to a car you need to be to take it
const MPH_PER_U = 4.92;        // 1 unit ≈ 2.2 m

function distToRoutes(x, z) {
  let best = Infinity;
  for (const r of CITY_ROUTES) {
    for (let i = 0; i < r.points.length - 1; i++) {
      const [ax, az] = r.points[i]; const [bx, bz] = r.points[i + 1];
      const vx = bx - ax, vz = bz - az; const L = vx * vx + vz * vz || 1;
      const t = Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / L));
      best = Math.min(best, Math.hypot(x - (ax + vx * t), z - (az + vz * t)));
    }
  }
  return best;
}

/** Kerbside parking spots from the road map. */
function findSpots(map, avoid) {
  const { cls, w, h, dEdge, toWorld, unitsPerTexel: u } = map;
  const cand = [];
  for (let r = 2; r < h - 2; r += 2) {
    for (let x = 2; x < w - 2; x += 2) {
      const i = r * w + x;
      if (cls[i] !== ROAD) continue;
      const de = dEdge[i] * u;
      if (de < 0.42 || de > 0.62) continue;
      const [wx, wz] = toWorld(x, r);
      if (distToRoutes(wx, wz) < 2.4) continue;
      if (avoid.some((a) => Math.hypot(a[0] - wx, a[1] - wz) < 4.5)) continue;
      // Along the road: perpendicular to the kerb's gradient.
      const gx = dEdge[i + 1] - dEdge[i - 1]; const gz = dEdge[i + w] - dEdge[i - w];
      const gl = Math.hypot(gx, gz);
      if (gl < 0.5) continue;
      // The lane must run straight for a car's length either side (no corners).
      const ax = -gz / gl; const az = gx / gl;
      let ok = true;
      for (const s of [-1.1, 1.1]) {
        const [tx, tr] = map.toTexel(wx + ax * s, wz + az * s);
        const j = tr * w + tx;
        if (cls[j] !== ROAD || Math.abs(dEdge[j] * u - de) > 0.2) { ok = false; break; }
      }
      if (!ok) continue;
      cand.push({ x: wx, z: wz, h: Math.atan2(ax, az) });
    }
  }
  if (!cand.length) return [];
  const out = [];
  // One near the arrival point, so the first thing a player can do is take it.
  const [sx, sz] = STREET_SPAWN.pos;
  const first = cand.reduce((b, c) => (Math.hypot(c.x - sx, c.z - sz) < Math.hypot(b.x - sx, b.z - sz) ? c : b), cand[0]);
  out.push(first);
  while (out.length < COUNT) {
    let best = null; let bd = -1;
    for (const c of cand) {
      const d = Math.min(...out.map((o) => Math.hypot(o.x - c.x, o.z - c.z)));
      if (d > bd) { bd = d; best = c; }
    }
    if (!best || bd < 18) break;
    out.push(best);
  }
  return out;
}

export default function Vehicles({ eye = 0.75 }) {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const carScale = useWorldStore((s) => s.carScale);
  const landmark = useWorldStore((s) => s.landmark);
  const [spots, setSpots] = useState(null);
  const groups = useRef([]);
  const near = useDriveStore((s) => s.near);
  const driving = useDriveStore((s) => s.driving);
  const setNear = useDriveStore((s) => s.setNear);
  const setDriving = useDriveStore((s) => s.setDriving);

  useEffect(() => roadMapBridge.subscribe((map) => {
    const avoid = landmark ? [[landmark[0], landmark[2]]] : [];
    const found = findSpots(map, avoid);
    console.log(`[drive] ${found.length} drivable cars parked at the kerb`);
    setSpots(found);
  }), [landmark]);

  // Physics state, one per car (parked cars keep theirs where you left them).
  const cars = useMemo(() => (spots || []).map((s, i) => ({
    x: s.x, z: s.z, h: s.h, vx: 0, vz: 0, steer: 0, paint: PAINTS[i % PAINTS.length],
  })), [spots]);

  // Dev handle: stand beside car i (for headless tests and tuning).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.__look) return undefined;
    window.__look.cars = cars;
    window.__look.standByCar = (i = 0) => { const c = cars[i]; if (!c) return false; camera.position.set(c.x + Math.cos(c.h) * 0.9, eye, c.z - Math.sin(c.h) * 0.9); camera.lookAt(c.x, eye * 0.6, c.z); return true; };
    return () => { delete window.__look.standByCar; };
  }, [cars, camera, eye]);

  // Parked cars are obstacles for walkers and for each other.
  useEffect(() => {
    const mine = [];
    cars.forEach((c, i) => { for (let k = 0; k < 3; k++) { const o = { x: c.x, z: c.z, r: 0.34, owner: `car${i}` }; obstacles.push(o); mine.push(o); } });
    return () => { for (const o of mine) { const j = obstacles.indexOf(o); if (j >= 0) obstacles.splice(j, 1); } };
  }, [cars]);
  const syncObstacles = (i, c) => {
    const fx = Math.sin(c.h); const fz = Math.cos(c.h);
    let k = 0;
    for (const o of obstacles) {
      if (o.owner !== `car${i}`) continue;
      const off = (k - 1) * 0.5; k += 1;
      o.x = c.x + fx * off; o.z = c.z + fz * off;
    }
  };

  // Input.
  const keys = useRef({});
  useEffect(() => {
    const down = (e) => {
      if (isTyping(e)) return;
      keys.current[e.code] = true;
      if (e.code === 'KeyF' && !e.repeat && !e.metaKey && !e.ctrlKey) requestToggle();
      if (driveBridge.active && e.code === 'Space') e.preventDefault();
    };
    const up = (e) => { keys.current[e.code] = false; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Drag to swing the camera around the car; it drifts back behind you after.
  const orbit = useRef({ yaw: 0, drag: null, idle: 0 });
  useEffect(() => {
    const dom = gl.domElement;
    const dn = (e) => { if (driveBridge.active) orbit.current.drag = { x: e.clientX, id: e.pointerId }; };
    const mv = (e) => {
      const d = orbit.current.drag; if (!d || d.id !== e.pointerId) return;
      orbit.current.yaw -= (e.clientX - d.x) * 0.006; d.x = e.clientX; orbit.current.idle = 0;
    };
    const upH = (e) => { if (orbit.current.drag?.id === e.pointerId) orbit.current.drag = null; };
    dom.addEventListener('pointerdown', dn);
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', upH);
    window.addEventListener('pointercancel', upH);
    return () => {
      dom.removeEventListener('pointerdown', dn);
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', upH);
      window.removeEventListener('pointercancel', upH);
    };
  }, [gl]);

  const active = useRef(-1);
  const camPos = useRef(new THREE.Vector3());
  const look = useRef(new THREE.Vector3());
  const shake = useRef(0);
  const baseFov = useRef(camera.fov);
  const push = useRef({ x: 0, z: 0 });
  const fwdV = useRef(new THREE.Vector3());
  const tmp = useMemo(() => ({ want: new THREE.Vector3(), from: new THREE.Vector3(), dir: new THREE.Vector3() }), []);

  const enter = (i) => {
    active.current = i;
    driveBridge.active = true;
    baseFov.current = camera.fov;
    camPos.current.copy(camera.position);
    orbit.current.yaw = 0;
    setDriving(i); setNear(null);
    door(); engineStart();
    window.dispatchEvent(new CustomEvent('ht:station-card'));
  };
  const exit = () => {
    const i = active.current; const c = cars[i];
    active.current = -1;
    driveBridge.active = false; driveBridge.speed = 0;
    c.vx = 0; c.vz = 0; c.steer = 0;
    // Step out on the driver's side, facing the way the car was pointing.
    const rx = Math.cos(c.h); const rz = -Math.sin(c.h);
    const out = { x: c.x - rx * 0.8, z: c.z - rz * 0.8 };
    camera.position.set(out.x, eye, out.z);
    camera.fov = baseFov.current; camera.updateProjectionMatrix();
    camera.lookAt(out.x + Math.sin(c.h) * 3, eye, out.z + Math.cos(c.h) * 3);
    setDriving(null);
    engineStop(); door();
  };

  // If anything else takes you somewhere (map travel, a scene, the ride),
  // park first.
  useEffect(() => useWorldStore.subscribe((s, prev) => {
    if (active.current < 0) return;
    if ((s.walkTarget && s.walkTarget !== prev.walkTarget) || (s.ride && !prev.ride) || s.interior) exit();
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (active.current >= 0) { driveBridge.active = false; engineStop(); } }, []);

  useFrame((state, rawDt) => {
    const dt = Math.min(rawDt, 0.05);
    const st = useWorldStore.getState();

    // ── WHICH CAR COULD YOU TAKE? ──────────────────────────────────────────
    if (active.current < 0) {
      let best = null; let bd = REACH;
      const pp = playerPos(camera);
      const px = pp.x; const pz = pp.z;
      cars.forEach((c, i) => { const d = Math.hypot(c.x - px, c.z - pz); if (d < bd) { bd = d; best = i; } });
      if (best !== near) setNear(best);
      const req = driveBridge.request; driveBridge.request = null;
      if (req === 'enter' && best != null && !st.ride && st.camSettled) enter(best);
      return;
    }

    const i = active.current; const c = cars[i];
    if (driveBridge.request === 'exit') { driveBridge.request = null; exit(); return; }
    driveBridge.request = null;

    // ── INPUT ───────────────────────────────────────────────────────────────
    const k = keys.current;
    let throttle = (k.KeyW || k.ArrowUp ? 1 : 0) - (k.KeyS || k.ArrowDown ? 1 : 0);
    let steerIn = (k.KeyA || k.ArrowLeft ? 1 : 0) - (k.KeyD || k.ArrowRight ? 1 : 0);
    throttle += moveBridge.y; steerIn -= moveBridge.x;
    throttle = Math.max(-1, Math.min(1, throttle)); steerIn = Math.max(-1, Math.min(1, steerIn));
    const handbrake = !!k.Space;

    // ── PHYSICS (arcade bicycle) ────────────────────────────────────────────
    const fx = Math.sin(c.h); const fz = Math.cos(c.h);
    const rx = Math.cos(c.h); const rz = -Math.sin(c.h);
    let vLong = c.vx * fx + c.vz * fz;
    let vLat = c.vx * rx + c.vz * rz;

    if (throttle > 0) {
      vLong += (vLong < -0.2 ? BRAKE : ACCEL * (1 - Math.max(0, vLong) / MAX_FWD * 0.6)) * throttle * dt;
    } else if (throttle < 0) {
      vLong += (vLong > 0.2 ? -BRAKE : -ACCEL * 0.6) * -throttle * dt;
    }
    vLong = Math.max(-MAX_REV, Math.min(MAX_FWD, vLong));
    // Rolling resistance + air.
    const drag = (0.8 + 0.018 * vLong * vLong) * dt;
    if (!throttle) vLong = Math.sign(vLong) * Math.max(0, Math.abs(vLong) - drag * 1.6);
    else vLong -= Math.sign(vLong) * Math.min(Math.abs(vLong), drag * 0.4);
    if (handbrake) vLong *= Math.exp(-1.6 * dt);

    // Steering: lighter at speed. Grip holds the car to its heading unless the
    // handbrake lets the back go.
    const speed = Math.abs(vLong);
    const maxSteer = THREE.MathUtils.lerp(0.62, 0.17, Math.min(1, speed / MAX_FWD));
    c.steer += (steerIn * maxSteer - c.steer) * Math.min(1, dt * 6);
    const yaw = (vLong / WHEELBASE) * Math.tan(c.steer) * (handbrake ? 1.45 : 1);
    c.h += yaw * dt;
    const grip = handbrake ? 1.4 : 9;
    vLat *= Math.exp(-grip * dt);
    const slide = Math.min(1, Math.abs(vLat) / 4);

    const nfx = Math.sin(c.h); const nfz = Math.cos(c.h);
    const nrx = Math.cos(c.h); const nrz = -Math.sin(c.h);
    c.vx = nfx * vLong + nrx * vLat;
    c.vz = nfz * vLong + nrz * vLat;
    c.x += c.vx * dt; c.z += c.vz * dt;

    // ── WALLS ───────────────────────────────────────────────────────────────
    let nx = 0; let nz = 0; let hit = 0;
    for (let s = -1; s <= 1; s++) {
      const off = s * 0.52;
      const d = pushOut(c.x + nfx * off, c.z + nfz * off, 0.36, push.current, `car${i}`);
      if (d > 0) { c.x += push.current.x; c.z += push.current.z; nx += push.current.x; nz += push.current.z; hit += d; }
    }
    const cb = st.cityBounds;
    if (cb) {
      const pad = 2.5;
      const bx = Math.min(cb.max[0] - pad, Math.max(cb.min[0] + pad, c.x));
      const bz = Math.min(cb.max[1] - pad, Math.max(cb.min[1] + pad, c.z));
      if (bx !== c.x || bz !== c.z) { nx += bx - c.x; nz += bz - c.z; hit += 0.1; c.x = bx; c.z = bz; }
    }
    if (hit > 0) {
      const nl = Math.hypot(nx, nz) || 1; const ux = nx / nl; const uz = nz / nl;
      const into = c.vx * ux + c.vz * uz;          // negative = moving into the wall
      if (into < 0) {
        c.vx -= ux * into * 1.25; c.vz -= uz * into * 1.25;   // a little bounce
        c.vx *= 0.82; c.vz *= 0.82;                          // and scrape
        const impact = -into;
        if (impact > 3.5) { crash(Math.min(1, impact / 12)); shake.current = Math.min(1, impact / 10); }
      }
    }
    syncObstacles(i, c);

    // ── THE CAR ITSELF ──────────────────────────────────────────────────────
    const g = groups.current[i];
    if (g) {
      g.position.set(c.x, 0, c.z);
      g.rotation.y = c.h;
      // Body roll into corners and a nose dip under braking — small, but it is
      // the difference between a car and a box on rails.
      const body = g.children[0];
      if (body) {
        body.rotation.z = THREE.MathUtils.lerp(body.rotation.z, -c.steer * Math.min(1, speed / 8) * 0.06, Math.min(1, dt * 6));
        body.rotation.x = THREE.MathUtils.lerp(body.rotation.x, (throttle < 0 && vLong > 1 ? 0.035 : throttle > 0 ? -0.015 : 0), Math.min(1, dt * 5));
      }
    }
    driveBridge.speed = vLong;
    driveBridge.rpm = Math.min(1, speed / MAX_FWD * 0.85 + Math.abs(throttle) * 0.15);
    driveBridge.mph = Math.round(speed * MPH_PER_U);
    engineSet(driveBridge.rpm, Math.abs(throttle), handbrake ? slide : slide * 0.5);

    // ── CHASE CAMERA ────────────────────────────────────────────────────────
    const o = orbit.current;
    if (!o.drag) { o.idle += dt; if (o.idle > 0.9) o.yaw *= Math.exp(-2.2 * dt); }
    const ch = c.h + o.yaw;
    const dist = 3.3 + speed * 0.05;
    const height = 1.25 + speed * 0.02;
    const want = tmp.want.set(c.x - Math.sin(ch) * dist, height, c.z - Math.cos(ch) * dist);
    // Keep the camera on this side of walls.
    const from = tmp.from.set(c.x, 0.9, c.z);
    const dir = tmp.dir.copy(want).sub(from); const len = dir.length(); dir.normalize();
    const wall = raycastWalls(from, dir, len);
    if (wall) want.copy(from).addScaledVector(dir, Math.max(0.6, wall.distance - 0.2));
    camPos.current.lerp(want, 1 - Math.exp(-9 * dt));
    camera.position.copy(camPos.current);
    if (shake.current > 0.001) {
      const s = shake.current * 0.08;
      camera.position.x += (Math.random() - 0.5) * s; camera.position.y += (Math.random() - 0.5) * s;
      shake.current *= Math.exp(-6 * dt);
    }
    look.current.set(c.x + nfx * 1.3, 0.55, c.z + nfz * 1.3);
    camera.lookAt(look.current);
    const fov = baseFov.current + Math.min(14, speed * 0.9);
    if (Math.abs(camera.fov - fov) > 0.05) { camera.fov += (fov - camera.fov) * Math.min(1, dt * 4); camera.updateProjectionMatrix(); }

    // Publish where "you" are — the car — for the radar, the area cards and
    // everyone else in the street.
    setCamPos({ x: c.x, y: eye, z: c.z });
    setCamBasis(fwdV.current.set(nfx, 0, nfz));
    emitMove([c.x, eye, c.z], c.h);
  });

  if (!spots || !spots.length) return null;
  const url = assetUrl('/models/cars/elise.glb');
  return (
    <group>
      {cars.map((c, i) => (
        <group key={i} ref={(gr) => { groups.current[i] = gr; }} position={[c.x, 0, c.z]} rotation={[0, c.h, 0]}>
          <group scale={carScale}>
            <SafeMount label="drivable car" retryMs={20000}>
              <Suspense fallback={null}>
                <CarModel url={url} length={CAR_LEN} rotY={Math.PI} tint={c.paint} />
              </Suspense>
            </SafeMount>
          </group>
          {NEXT_LOOK && (
            <CarLights
              carRef={{ get current() { return groups.current[i]; } }}
              bodyRef={{ get current() { return groups.current[i]?.children[0]; } }}
              lit={driving === i}
            />
          )}
        </group>
      ))}
    </group>
  );
}
